import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { MultiFillTable } from './exception-information-section'
import { normalizeExceptionValue } from './form-utils'
import { toExceptionKey } from '../../common/exception-utils'

const cardSx = {
  border: '1px solid #C7D7DC',
  borderRadius: 1,
  backgroundColor: '#FFFFFF',
}

const cardContentSx = {
  px: 2,
  py: 1.5,
}

const driverHistoryConfig = {
  key: 'driver',
  label: 'Driver',
  columns: [
    { key: 'date', label: '', width: '90px' },
    { key: 'exception', label: '', width: '1fr' },
    { key: 'value', label: '', width: '120px' },
    { key: 'action', label: '', width: '60px' },
  ],
  emptyMessage: 'No other driver exceptions.',
}

const vehicleHistoryConfig = {
  key: 'vehicle',
  label: 'Vehicle',
  columns: [
    { key: 'date', label: '', width: '90px' },
    { key: 'exception', label: '', width: '1fr' },
    { key: 'value', label: '', width: '140px' },
    { key: 'action', label: '', width: '60px' },
  ],
  emptyMessage: 'No other vehicle exceptions.',
}

const HISTORY_TABS = [driverHistoryConfig, vehicleHistoryConfig]

const formatHistoryDateValue = (value) => {
  if (!value) {
    return '-'
  }

  const candidate = moment(value)
  if (candidate.isValid()) {
    return candidate.format('DD MMM YY')
  }

  return value
}

const toDateKey = (value) => {
  if (!value) {
    return ''
  }

  const dateMoment = moment(value)
  return dateMoment.isValid() ? dateMoment.format('YYYY-MM-DD') : `${value}`
}

const createHistoryRows = (items, valueSelector, currentKey, currentDateKey) => {
  if (!Array.isArray(items)) {
    return []
  }

  const multiFillDates = new Set()
  return items.reduce((acc, item) => {
    if (!item) {
      return acc
    }

    const exceptionKey = toExceptionKey(item)
    if (currentKey && exceptionKey === currentKey) {
      return acc
    }

    const dateKey = toDateKey(item.transactionDate)
    const normalizedException = normalizeExceptionValue(item.exception)
    if (
      (normalizedException === 'multifill' || normalizedException === 'multifills') &&
      dateKey
    ) {
      if (multiFillDates.has(dateKey)) {
        return acc
      }
      multiFillDates.add(dateKey)
    }

    acc.push({
      key:
        exceptionKey ||
        `${item.agreementNumber || ''}_${item.transactionDate || ''}_${item.transactionTime || ''}`,
      formattedDate: formatHistoryDateValue(item.transactionDate),
      exception: item.exception || '-',
      value: valueSelector(item),
      raw: item,
      // The record being viewed is already on screen, so its date shouldn't show a "view" action
      hideView: Boolean(currentDateKey) && dateKey === currentDateKey,
    })

    return acc
  }, [])
}

const HistoryPanel = ({
  activeTab,
  onTabChange,
  driverRows,
  vehicleRows,
  status,
  error,
  onView,
}) => {
  const tabConfig =
    activeTab === vehicleHistoryConfig.key ? vehicleHistoryConfig : driverHistoryConfig
  const rows = activeTab === vehicleHistoryConfig.key ? vehicleRows : driverRows
  const columnTemplate = tabConfig.columns.map((column) => column.width).join(' ')
  const isLoading = status === 'processing'
  const hasError = status === 'error' && error
  const isEmpty = !isLoading && !hasError && rows.length === 0

  return (
    <Box sx={cardSx}>
      <Box sx={cardContentSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {HISTORY_TABS.map((tab, index) => (
            <React.Fragment key={tab.key}>
              <Box
                component="button"
                type="button"
                onClick={() => tab.key !== activeTab && onTabChange(tab.key)}
                sx={{
                  appearance: 'none',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: tab.key === activeTab ? 'default' : 'pointer',
                  color: tab.key === activeTab ? '#111827' : '#6B7280',
                  textDecoration: tab.key === activeTab ? 'underline' : 'none',
                  fontSize: 12,
                  fontWeight: 600,
                }}>
                {tab.label}
              </Box>
              {index < HISTORY_TABS.length - 1 && (
                <Typography component="span" sx={{ fontSize: 12, color: '#6B7280' }}>
                  |
                </Typography>
              )}
            </React.Fragment>
          ))}
        </Box>

        <Box sx={{ maxHeight: 70, overflowY: 'auto' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: columnTemplate,
              gap: 1,
              pb: 1,
              borderBottom: '1px solid #E0E0E0',
            }}>
            {tabConfig.columns.map((column) => (
              <Typography
                key={column.key}
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#617282',
                }}>
                {column.label}
              </Typography>
            ))}
          </Box>

          {isLoading && (
            <Typography sx={{ fontSize: 12, color: '#6B7280', mt: 1 }}>
              Loading history…
            </Typography>
          )}

          {hasError && (
            <Typography sx={{ fontSize: 12, color: '#F04438', mt: 1 }}>
              {error}
            </Typography>
          )}

          {isEmpty && (
            <Typography sx={{ fontSize: 12, color: '#6B7280', mt: 1 }}>
              {tabConfig.emptyMessage}
            </Typography>
          )}

          {!isLoading &&
            !hasError &&
            rows.map((row) => (
              <Box
                key={row.key}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: columnTemplate,
                  gap: 1,
                  py: 0.75,
                  borderBottom: '1px solid #F0F3F5',
                  '&:last-of-type': { borderBottom: 'none' },
                  fontSize: 12,
                  alignItems: 'center',
                }}>
                <span>{row.formattedDate}</span>
                <span>{row.exception}</span>
                <span>{row.value}</span>
                {!row.hideView && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => onView && onView(row.raw)}
                    sx={{ textTransform: 'none', fontSize: 12, minWidth: 0, padding: 0 }}>
                    view
                  </Button>
                )}
              </Box>
            ))}
        </Box>
      </Box>
    </Box>
  )
}

const NotesHistory = ({ notes }) => (
  <Box sx={cardSx}>
    <Box sx={{ ...cardContentSx, maxHeight: 120, overflowY: 'auto' }}>
      {notes.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: '#888' }}>
          No notes yet.
        </Typography>
      ) : (
        notes.map((note) => {
          const createdMoment = moment(note.createdAt)
          const fallbackDateMoment = moment(note.transactionDate)
          const formattedDate = createdMoment.isValid()
            ? createdMoment.format('DD/MM/YY')
            : fallbackDateMoment.isValid()
              ? fallbackDateMoment.format('DD/MM/YY')
              : ''
          const formattedTime = createdMoment.isValid()
            ? createdMoment.format('h:mma')
            : note.transactionTime || ''
          const timestamp = [formattedTime, formattedDate].filter(Boolean).join(' ')
          const author = note.createdBy || 'Unknown'
          const timestampLabel = timestamp ? `${timestamp} ` : ''
          return (
            <Box key={note.id} sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5,
                }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                  {timestampLabel}Added by {author}
                </Typography>

              </Box>

              <Typography sx={{ fontSize: 12, whiteSpace: 'pre-line' }}>
                {note.noteText}
              </Typography>
            </Box>
          )
        })
      )}
    </Box>
  </Box>
)

NotesHistory.propTypes = {
  notes: PropTypes.arrayOf(PropTypes.object).isRequired,
}

const NotesEditor = ({ editingNoteId, cancelEditingNote }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
      {editingNoteId ? 'Edit Note' : 'Add a Note'}
    </Typography>
    {editingNoteId && (
      <Button
        variant="text"
        color="primary"
        onClick={cancelEditingNote}
        sx={{ textTransform: 'none' }}>
        Cancel edit
      </Button>
    )}
  </Box>
)

const NotesTextField = ({
  values,
  notePlaceholder,
  onNoteChange,
  noteInputRef,
  disabled,
}) => (
  <Box sx={{ ...cardSx, gap: 1, mt: 1 }}>
    <Box sx={cardContentSx}>
      <TextField
        fullWidth
        multiline
        minRows={5}
        maxRows={5}
        value={values?.notes || ''}
        onChange={onNoteChange}
        disabled={disabled}
        placeholder={notePlaceholder}
        variant="standard"
        InputProps={{ disableUnderline: true }}
        inputRef={noteInputRef}
        sx={{ fontSize: 12 }}/>
    </Box>
  </Box>
)

NotesTextField.propTypes = {
  values: PropTypes.object.isRequired,
  notePlaceholder: PropTypes.string.isRequired,
  onNoteChange: PropTypes.func.isRequired,
  noteInputRef: PropTypes.shape({ current: PropTypes.any }),
  disabled: PropTypes.bool,
}

NotesTextField.defaultProps = {
  noteInputRef: undefined,
  disabled: false,
}

NotesEditor.propTypes = {
  editingNoteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cancelEditingNote: PropTypes.func.isRequired,
}

NotesEditor.defaultProps = {
  editingNoteId: null,
}

const NotesSection = ({
  isMultiFill,
  notePlaceholder,
  values,
  onNoteChange,
  noteInputRef,
  noteInputDisabled,
  editingNoteId,
  cancelEditingNote,
  notes,
  exceptionTitle,
  issueSummaryText,
  multiFillRows,
  formatDateTime,
  formatLitresWithValue,
  getOverTankCapacityLabel,
  odometerFormatter,
  driverHistory,
  vehicleHistory,
  historyStatus,
  historyError,
  onViewHistoryItem,
  currentExceptionKey,
  currentExceptionDate,
}) => {
  const showMultiFillTable = isMultiFill && multiFillRows.length > 0
  const [activeHistoryTab, setActiveHistoryTab] = useState('driver')
  const currentDateKey = useMemo(() => toDateKey(currentExceptionDate), [currentExceptionDate])

  const driverHistoryRows = useMemo(
    () =>
      createHistoryRows(
        driverHistory,
        (item) => item.vehicleRegistration || '-',
        currentExceptionKey,
        currentDateKey
      ),
    [driverHistory, currentExceptionKey, currentDateKey]
  )

  const vehicleHistoryRows = useMemo(
    () =>
      createHistoryRows(
        vehicleHistory,
        (item) => item.driverName || '-',
        currentExceptionKey,
        currentDateKey
      ),
    [vehicleHistory, currentExceptionKey, currentDateKey]
  )

  return (
    <Box>
      <Grid container alignItems="flex-start">
        <Grid item xs={12} md={isMultiFill ? 12 : 6} sx={{ pr: isMultiFill ? 3 : 3 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
            Exception issue
          </Typography>

          <Box sx={cardSx}>
            <Box sx={cardContentSx}>
              {isMultiFill ? (
                showMultiFillTable && (
                  <MultiFillTable
                    rows={multiFillRows}
                    formatDateTime={formatDateTime}
                    formatLitresWithValue={formatLitresWithValue}
                    getOverTankCapacityLabel={getOverTankCapacityLabel}
                    odometerFormatter={odometerFormatter}
                    maxHeight={177}/>
                )
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#F04438',
                        mb: 1,
                      }}>
                      {exceptionTitle}
                    </Typography>

                    <Typography sx={{ fontSize: 12, lineHeight: '18px' }}>
                      {issueSummaryText}
                    </Typography>
                  </Box>

                  <WarningAmberIcon sx={{ color: '#F04438', mt: '2px' }} />
                </Box>
              )}
            </Box>
          </Box>
        </Grid>

        {!isMultiFill && (
          <Grid item xs={12} md={6}>
            <NotesEditor
              editingNoteId={editingNoteId}
              cancelEditingNote={cancelEditingNote}/>
            <NotesTextField
              values={values}
              notePlaceholder={notePlaceholder}
              onNoteChange={onNoteChange}
              noteInputRef={noteInputRef}
              disabled={noteInputDisabled}/>
          </Grid>
        )}
      </Grid>

      {isMultiFill && (
        <Grid container alignItems="flex-start" sx={{ mt: 2 }}>
          <Grid item xs={12} md={6} sx={{ pr: 3 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
              History
            </Typography>
            <HistoryPanel
              activeTab={activeHistoryTab}
              onTabChange={setActiveHistoryTab}
              driverRows={driverHistoryRows}
              vehicleRows={vehicleHistoryRows}
              status={historyStatus}
              error={historyError}
              onView={onViewHistoryItem}/>
          </Grid>
          <Grid item xs={12} md={6}>
            <NotesEditor
              editingNoteId={editingNoteId}
              cancelEditingNote={cancelEditingNote}/>
            <NotesTextField
              values={values}
              notePlaceholder={notePlaceholder}
              onNoteChange={onNoteChange}
              noteInputRef={noteInputRef}
              disabled={noteInputDisabled}/>
          </Grid>
          <Grid item xs={12} md={6} />
          <Grid item xs={12} md={6}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
              Notes History
            </Typography>
            <NotesHistory
              notes={notes}/>
          </Grid>
        </Grid>
      )}

      {!isMultiFill && (
        <Grid container alignItems="flex-start" sx={{ mt: 2 }}>
          <Grid item xs={12} md={6} sx={{ pr: 3 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
              History
            </Typography>
            <HistoryPanel
              activeTab={activeHistoryTab}
              onTabChange={setActiveHistoryTab}
              driverRows={driverHistoryRows}
              vehicleRows={vehicleHistoryRows}
              status={historyStatus}
              error={historyError}
              onView={onViewHistoryItem}/>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
              Notes History
            </Typography>
            <NotesHistory
              notes={notes}/>
          </Grid>
        </Grid>
      )}
    </Box>
  )
}

NotesSection.propTypes = {
  isMultiFill: PropTypes.bool.isRequired,
  notePlaceholder: PropTypes.string.isRequired,
  values: PropTypes.object.isRequired,
  onNoteChange: PropTypes.func.isRequired,
  noteInputRef: PropTypes.shape({ current: PropTypes.any }),
  noteInputDisabled: PropTypes.bool,
  editingNoteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cancelEditingNote: PropTypes.func.isRequired,
  notes: PropTypes.arrayOf(PropTypes.object).isRequired,
  exceptionTitle: PropTypes.string.isRequired,
  issueSummaryText: PropTypes.string.isRequired,
  multiFillRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  formatDateTime: PropTypes.func.isRequired,
  formatLitresWithValue: PropTypes.func.isRequired,
  getOverTankCapacityLabel: PropTypes.func.isRequired,
  odometerFormatter: PropTypes.instanceOf(Intl.NumberFormat).isRequired,
  driverHistory: PropTypes.arrayOf(PropTypes.object),
  vehicleHistory: PropTypes.arrayOf(PropTypes.object),
  historyStatus: PropTypes.string,
  historyError: PropTypes.string,
  onViewHistoryItem: PropTypes.func,
  currentExceptionKey: PropTypes.string,
  currentExceptionDate: PropTypes.string,
}

NotesSection.defaultProps = {
  noteInputRef: undefined,
  noteInputDisabled: false,
  editingNoteId: null,
  driverHistory: [],
  vehicleHistory: [],
  historyStatus: 'none',
  historyError: null,
  onViewHistoryItem: undefined,
  currentExceptionKey: '',
  currentExceptionDate: '',
}

export default NotesSection
